/**
 * ActionBarMenu.tsx
 *
 * Per-bar page indicator/arrows and the small bar menu (⚙ button or right-click
 * on the bar edge) for Stage 1 of the controls, bars and HUD plan:
 *  - Add / remove a page on this bar; flip pages
 *  - Add / remove an extra bar; show / hide a bar; move an extra bar to a spot (above, right, left)
 *  - Asks confirmation before removing a non-empty bar or page
 *
 * Uses existing Tome tokens (--tome-hud-*, --tome-gilt, --tome-cut-*) with theme fallbacks.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  barSlotCount,
  MAX_BARS,
  MAX_PAGES_PER_BAR,
  SLOTS_PER_PAGE,
  type BarSpot,
  type BarState,
} from '../../../../shared/hotbar/barLayout';
import { ACCENT, COLORS, FONTS, FONT_SIZE, FONT_WEIGHT, INK, RADII, SPACE } from '../../theme';

export type ExtraBarSpot = Exclude<BarSpot, 'main'>;

export type ActionBarLayoutEdit =
  | { op: 'addBar'; spot?: ExtraBarSpot }
  | { op: 'removeBar'; bar: number }
  | { op: 'addPage'; bar: number }
  | { op: 'removePage'; bar: number; page: number }
  | { op: 'setPage'; bar: number; page: number }
  | { op: 'setVisible'; bar: number; visible: boolean }
  | { op: 'setSpot'; bar: number; spot: ExtraBarSpot }
  | { op: 'setSlots'; bar: number; slots: number };

const SLOT_COUNT_OPTIONS = Array.from({ length: SLOTS_PER_PAGE }, (_, i) => i + 1);

export interface ActionBarControlsProps {
  barState: BarState;
  allBars: ReadonlyArray<BarState>;
  locked: boolean;
  lockText: string | null;
  currentPageHasItems: boolean;
  barHasItems: boolean;
  menuOpen: boolean;
  onToggleMenu: () => void;
  onCloseMenu: () => void;
  onLayoutEdit?: (edit: ActionBarLayoutEdit) => void;
  orientation?: 'horizontal' | 'vertical';
  dragLocked?: boolean;
  onToggleDragLock?: () => void;
}

const SPOT_LABELS: Record<ExtraBarSpot, string> = {
  above: 'Above main bar',
  right: 'Right edge',
  left: 'Left edge',
};

export const ActionBarControls: React.FC<ActionBarControlsProps> = ({
  barState,
  allBars,
  locked,
  lockText,
  currentPageHasItems,
  barHasItems,
  menuOpen,
  onToggleMenu,
  onCloseMenu,
  onLayoutEdit,
  orientation = 'horizontal',
  dragLocked = true,
  onToggleDragLock,
}) => {
  const { id: barId, pages, currentPage, spot } = barState;
  const slotCount = barSlotCount(barState);
  const [confirmKind, setConfirmKind] = useState<'page' | 'bar' | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) {
      setConfirmKind(null);
      return;
    }
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        onCloseMenu();
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseMenu();
    };
    window.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen, onCloseMenu]);

  const prevPage = ((currentPage + pages - 2) % pages) + 1;
  const nextPage = (currentPage % pages) + 1;
  const hiddenBars = allBars.filter((b) => b.id > 1 && !b.visible);
  const canAddBar = !locked && allBars.length < MAX_BARS;
  const canAddPage = !locked && pages < MAX_PAGES_PER_BAR;

  const fire = (edit: ActionBarLayoutEdit) => {
    onLayoutEdit?.(edit);
    setConfirmKind(null);
    onCloseMenu();
  };

  const handleRemovePageClick = () => {
    if (currentPageHasItems) {
      setConfirmKind('page');
    } else {
      fire({ op: 'removePage', bar: barId, page: currentPage });
    }
  };

  const handleRemoveBarClick = () => {
    if (barHasItems) {
      setConfirmKind('bar');
    } else {
      fire({ op: 'removeBar', bar: barId });
    }
  };

  return (
    <div
      ref={rootRef}
      className="hudt-bar-controls"
      style={orientation === 'vertical' ? controlsColumnStyle : controlsRowStyle}
    >
      {pages > 1 && !locked && (
        <div
          className="hudt-page-cluster"
          data-testid={`bar-${barId}-page-controls`}
          style={orientation === 'vertical' ? pageClusterVerticalStyle : pageClusterStyle}
        >
          <button
            type="button"
            data-testid={`bar-${barId}-prev-page`}
            aria-label={`Previous page on Bar ${barId}`}
            disabled={locked}
            onClick={() => onLayoutEdit?.({ op: 'setPage', bar: barId, page: prevPage })}
            style={iconBtnStyle}
            title="Previous page"
          >
            ◀
          </button>
          <span
            data-testid={`bar-${barId}-page-indicator`}
            style={pageLabelStyle}
          >
            page {currentPage} / {pages}
          </span>
          <button
            type="button"
            data-testid={`bar-${barId}-next-page`}
            aria-label={`Next page on Bar ${barId}`}
            disabled={locked}
            onClick={() => onLayoutEdit?.({ op: 'setPage', bar: barId, page: nextPage })}
            style={iconBtnStyle}
            title="Next page"
          >
            ▶
          </button>
        </div>
      )}

      {!locked && onToggleDragLock && (
        <button
          type="button"
          data-testid={`bar-${barId}-lock-button`}
          aria-label={dragLocked ? `Unlock Bar ${barId} slot dragging` : `Lock Bar ${barId} slot dragging`}
          aria-pressed={dragLocked}
          onClick={onToggleDragLock}
          style={dragLocked ? gearBtnStyle : unlockedBtnStyle}
          title={
            dragLocked
              ? 'Action bar locked — hold Shift to drag slots off, or click to unlock'
              : 'Action bar unlocked — click to lock slot drag-off'
          }
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3.5" y="7.5" width="9" height="6.5" rx="1.5" />
            {dragLocked ? (
              <path d="M5.5 7.5V5.2a2.5 2.5 0 0 1 5 0v2.3" />
            ) : (
              <path d="M5.5 7.5V5.2a2.5 2.5 0 0 1 4.6-1.3" />
            )}
          </svg>
        </button>
      )}

      <button
        type="button"
        data-testid={`bar-${barId}-menu-button`}
        aria-label={`Bar ${barId} options`}
        aria-expanded={menuOpen}
        onClick={onToggleMenu}
        style={gearBtnStyle}
        title={`Bar ${barId} options (or right-click bar edge)`}
      >
        ⚙
      </button>

      {menuOpen && (
        <div
          role="menu"
          aria-label={`Bar ${barId} menu`}
          data-testid={`bar-${barId}-menu`}
          style={
            orientation === 'vertical'
              ? (spot === 'right' ? menuPopoverLeftStyle : menuPopoverRightStyle)
              : menuPopoverStyle
          }
        >
          <div style={menuHeaderStyle}>
            <span>Bar {barId}{barId === 1 ? ' · Main' : ''}</span>
            <span style={menuSubStyle}>Page {currentPage} of {pages}</span>
          </div>

          {locked ? (
            <div data-testid={`bar-${barId}-menu-locked`} style={lockedNoticeStyle}>
              🔒 {lockText ?? 'This bar cannot be changed right now'}
            </div>
          ) : confirmKind ? (
            <div data-testid={`bar-${barId}-confirm-dialog`} style={confirmBoxStyle}>
              <div style={confirmTextStyle}>
                {confirmKind === 'page'
                  ? `Page ${currentPage} on Bar ${barId} has abilities or items. Remove it?`
                  : `Bar ${barId} has abilities or items. Remove it?`}
              </div>
              <div style={confirmBtnRowStyle}>
                <button
                  type="button"
                  data-testid={`bar-${barId}-confirm-remove`}
                  onClick={() => (
                    confirmKind === 'page'
                      ? fire({ op: 'removePage', bar: barId, page: currentPage })
                      : fire({ op: 'removeBar', bar: barId })
                  )}
                  style={confirmDangerBtnStyle}
                >
                  Remove
                </button>
                <button
                  type="button"
                  data-testid={`bar-${barId}-cancel-remove`}
                  onClick={() => setConfirmKind(null)}
                  style={confirmCancelBtnStyle}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <div style={menuSectionLabelStyle}>Slots ({slotCount}/{SLOTS_PER_PAGE})</div>
              <div style={spotRowStyle} data-testid={`bar-${barId}-slot-count-controls`}>
                {SLOT_COUNT_OPTIONS.map((count) => (
                  <button
                    key={count}
                    type="button"
                    role="menuitemradio"
                    aria-checked={slotCount === count}
                    aria-label={`${count} slot${count === 1 ? '' : 's'} on Bar ${barId}`}
                    data-testid={`bar-${barId}-slots-${count}`}
                    onClick={() => fire({ op: 'setSlots', bar: barId, slots: count })}
                    style={slotCount === count ? spotBtnActiveStyle : spotBtnStyle}
                  >
                    {count}
                  </button>
                ))}
              </div>

              <div style={menuDividerStyle} />
              <div style={menuSectionLabelStyle}>Pages</div>
              <button
                type="button"
                role="menuitem"
                data-testid={`bar-${barId}-add-page`}
                disabled={!canAddPage}
                onClick={() => fire({ op: 'addPage', bar: barId })}
                style={canAddPage ? menuItemStyle : menuItemDisabledStyle}
              >
                + Add page ({pages}/{MAX_PAGES_PER_BAR})
              </button>
              {pages > 1 && !locked && (
                <button
                  type="button"
                  role="menuitem"
                  data-testid={`bar-${barId}-remove-page`}
                  onClick={handleRemovePageClick}
                  style={menuItemDangerStyle}
                >
                  − Remove page {currentPage}
                </button>
              )}

              <div style={menuDividerStyle} />
              <div style={menuSectionLabelStyle}>Action Bars</div>
              <button
                type="button"
                role="menuitem"
                data-testid={`bar-${barId}-add-bar`}
                disabled={!canAddBar}
                onClick={() => fire({ op: 'addBar' })}
                style={canAddBar ? menuItemStyle : menuItemDisabledStyle}
              >
                + Add action bar ({allBars.length}/{MAX_BARS})
              </button>

              {barId > 1 && (
                <>
                  <div style={menuSectionLabelStyle}>Position</div>
                  <div style={spotRowStyle}>
                    {(['above', 'right', 'left'] as const).map((candidate) => (
                      <button
                        key={candidate}
                        type="button"
                        role="menuitemradio"
                        aria-checked={spot === candidate}
                        data-testid={`bar-${barId}-spot-${candidate}`}
                        onClick={() => fire({ op: 'setSpot', bar: barId, spot: candidate })}
                        style={spot === candidate ? spotBtnActiveStyle : spotBtnStyle}
                      >
                        {SPOT_LABELS[candidate]}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    role="menuitem"
                    data-testid={`bar-${barId}-hide`}
                    onClick={() => fire({ op: 'setVisible', bar: barId, visible: false })}
                    style={menuItemStyle}
                  >
                    Hide Bar {barId}
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    data-testid={`bar-${barId}-remove-bar`}
                    onClick={handleRemoveBarClick}
                    style={menuItemDangerStyle}
                  >
                    − Remove Bar {barId}
                  </button>
                </>
              )}

              {hiddenBars.length > 0 && (
                <>
                  <div style={menuDividerStyle} />
                  <div style={menuSectionLabelStyle}>Hidden Bars</div>
                  {hiddenBars.map((hidden) => (
                    <button
                      key={hidden.id}
                      type="button"
                      role="menuitem"
                      data-testid={`bar-${barId}-show-bar-${hidden.id}`}
                      onClick={() => fire({ op: 'setVisible', bar: hidden.id, visible: true })}
                      style={menuItemStyle}
                    >
                      Show Bar {hidden.id}
                    </button>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const controlsRowStyle: React.CSSProperties = { position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 2 };
const controlsColumnStyle: React.CSSProperties = { position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, marginTop: 2 };
const pageClusterStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 5px',
  borderRadius: 'var(--tome-chip-cut-2, 4px)',
  background: 'radial-gradient(circle at 50% 30%, var(--tome-cover-hi, #2d4227), var(--tome-cover-lo, #121c11))',
  border: `1px solid var(--tome-hud-rule, ${ACCENT.brass})`,
  boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.65), 0 1px 3px rgba(0, 0, 0, 0.4)',
};
const pageClusterVerticalStyle: React.CSSProperties = { ...pageClusterStyle, flexDirection: 'column', padding: '3px 2px' };
const pageLabelStyle: React.CSSProperties = {
  fontSize: FONT_SIZE.xs, fontFamily: FONTS.ui, fontWeight: FONT_WEIGHT.semibold,
  color: 'var(--tome-hud-ink, #f0e6d2)', whiteSpace: 'nowrap', padding: '0 2px',
};
const iconBtnStyle: React.CSSProperties = {
  background: 'transparent', border: 'none', color: 'var(--tome-gilt-bright, #e5c069)',
  fontSize: 10, lineHeight: 1, padding: '2px 3px', cursor: 'pointer',
};
const gearBtnStyle: React.CSSProperties = {
  width: 24, height: 24, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  borderRadius: 'var(--tome-chip-cut-2, 4px)',
  background: 'radial-gradient(circle at 50% 30%, var(--tome-cover-hi, #2d4227), var(--tome-cover-lo, #121c11))',
  border: `1px solid var(--tome-hud-rule, ${ACCENT.brass})`,
  boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.65), 0 1px 3px rgba(0, 0, 0, 0.4)',
  color: 'var(--tome-gilt-bright, #e5c069)',
  fontSize: 12, lineHeight: 1, padding: 0, cursor: 'pointer',
};
const unlockedBtnStyle: React.CSSProperties = {
  ...gearBtnStyle,
  borderColor: '#70ffa0',
  boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.65), 0 0 6px rgba(112, 255, 160, 0.35)',
};
const menuPopoverStyle: React.CSSProperties = {
  position: 'absolute', bottom: 'calc(100% + 8px)', right: 0, minWidth: 210, padding: SPACE.sm,
  borderRadius: `var(--tome-cut-2, ${RADII.md}px)`,
  background: `linear-gradient(180deg, var(--tome-cover-hi, #2d4227), var(--tome-cover-lo, ${INK.base}))`,
  border: `1px solid var(--tome-gilt, ${ACCENT.brass})`,
  boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.6), inset 0 0 0 2px var(--tome-cover-lo, #121c11), inset 0 0 0 3px var(--tome-hud-rule, rgba(185, 138, 46, 0.55)), 0 8px 20px rgba(0, 0, 0, 0.65)',
  color: `var(--tome-hud-ink, ${COLORS.textPrimary})`, fontFamily: FONTS.ui, fontSize: FONT_SIZE.xs,
  zIndex: 50, display: 'flex', flexDirection: 'column', gap: 4,
};
const menuPopoverLeftStyle: React.CSSProperties = { ...menuPopoverStyle, bottom: 0, right: 'calc(100% + 8px)' };
const menuPopoverRightStyle: React.CSSProperties = { ...menuPopoverStyle, bottom: 0, left: 'calc(100% + 8px)', right: 'auto' };
const menuHeaderStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 4,
  borderBottom: `1px solid var(--tome-hud-rule, ${ACCENT.brass})`, fontFamily: FONTS.display,
  fontWeight: FONT_WEIGHT.bold, color: 'var(--tome-gilt, #e5c07b)',
};
const menuSubStyle: React.CSSProperties = { fontFamily: FONTS.ui, fontSize: 10, color: `var(--tome-hud-ink-soft, ${COLORS.textSecondary})` };
const menuSectionLabelStyle: React.CSSProperties = {
  fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em',
  color: `var(--tome-hud-ink-soft, ${COLORS.textSecondary})`, marginTop: 2,
};
const menuDividerStyle: React.CSSProperties = { height: 1, background: `var(--tome-hud-rule, ${ACCENT.brass})`, opacity: 0.4, margin: '2px 0' };
const menuItemStyle: React.CSSProperties = {
  textAlign: 'left', padding: '5px 8px', borderRadius: RADII.sm, background: 'rgba(0, 0, 0, 0.3)',
  border: '1px solid rgba(196, 164, 105, 0.22)', color: `var(--tome-hud-ink, ${COLORS.textPrimary})`,
  fontSize: FONT_SIZE.xs, fontFamily: FONTS.ui, cursor: 'pointer',
};
const menuItemDisabledStyle: React.CSSProperties = { ...menuItemStyle, opacity: 0.45, cursor: 'not-allowed' };
const menuItemDangerStyle: React.CSSProperties = { ...menuItemStyle, color: COLORS.danger, borderColor: 'rgba(255, 71, 87, 0.35)' };
const spotRowStyle: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 4 };
const spotBtnStyle: React.CSSProperties = {
  padding: '3px 6px', borderRadius: RADII.sm, background: 'rgba(0, 0, 0, 0.28)',
  border: '1px solid rgba(196, 164, 105, 0.25)', color: `var(--tome-hud-ink-soft, ${COLORS.textSecondary})`,
  fontSize: 10, cursor: 'pointer',
};
const spotBtnActiveStyle: React.CSSProperties = {
  ...spotBtnStyle, background: 'rgba(196, 164, 105, 0.22)', borderColor: 'var(--tome-gilt, #e5c07b)',
  color: `var(--tome-hud-ink, ${COLORS.textPrimary})`, fontWeight: FONT_WEIGHT.bold,
};
const lockedNoticeStyle: React.CSSProperties = {
  padding: '6px 8px', borderRadius: RADII.sm, background: 'rgba(0, 0, 0, 0.35)',
  color: COLORS.textAccent, fontSize: FONT_SIZE.xs,
};
const confirmBoxStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 6, padding: 6, borderRadius: RADII.sm,
  background: 'rgba(255, 71, 87, 0.1)', border: `1px solid ${COLORS.danger}`,
};
const confirmTextStyle: React.CSSProperties = { fontSize: FONT_SIZE.xs, lineHeight: 1.35 };
const confirmBtnRowStyle: React.CSSProperties = { display: 'flex', gap: 6, justifyContent: 'flex-end' };
const confirmDangerBtnStyle: React.CSSProperties = {
  padding: '4px 8px', borderRadius: RADII.sm, background: COLORS.danger, border: 'none',
  color: '#fff', fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.bold, cursor: 'pointer',
};
const confirmCancelBtnStyle: React.CSSProperties = {
  padding: '4px 8px', borderRadius: RADII.sm, background: 'rgba(0, 0, 0, 0.4)',
  border: '1px solid rgba(255, 255, 255, 0.2)', color: COLORS.textPrimary, fontSize: FONT_SIZE.xs, cursor: 'pointer',
};
